/**
 * THE REEL'S SCENES — five pieces of work Leveri does today, drawn in its own
 * screens' words at a fixed 360 × 296 and scaled by the reel like a film.
 *
 * Only shipped work is shown: the plan before work starts, statement reading
 * that admits a line only when the statement's running balance agrees, bank
 * reconciliation that names what is left, draft accounts from bank lines by
 * pattern, and the client ask that resumes the same work. The wording is the
 * product's own (the plan card, the procedure steps, the section heads, the
 * review acts); the clients and figures are invented (figures.ts).
 *
 * Every scene is a pure function of its progress `p`. Lines that have not yet
 * arrived are drawn transparent, so a scene's layout never jumps as it plays.
 * The only saturated fill is a pressed button, and only for the moment it is
 * pressed: on a page, the page's own action stays the one saturated thing.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ACME, HARBOUR } from "./figures.js";
import { appear, breathe, caretOn, clamp01, counted, easeInOut, span, typed } from "./timeline.js";

/** The marker fills, by the names this build's tokens use. */
const MARK = { est: "bg-est", att: "bg-att", prog: "bg-prog" } as const;
type Tone = keyof typeof MARK;

export const STAGE = { width: 360, height: 296 } as const;

/** A state square. `breath` (0–1 opacity) is given only while the scene shows work happening. */
function Mark({ tone, breath }: { tone: Tone; breath?: number | undefined }) {
  return <span aria-hidden="true" className={`mt-[4px] inline-block h-[7px] w-[7px] shrink-0 rounded-[1px] ${MARK[tone]}`} style={breath === undefined ? undefined : { opacity: breath }} />;
}

function State({ tone, x, breath, children }: { tone: Tone; x: number; breath?: number | undefined; children: ReactNode }) {
  return (
    <p className="flex items-start gap-2 text-[11.5px] leading-[1.45] text-ink" style={appear(x)}>
      <Mark tone={tone} breath={breath} /><span>{children}</span>
    </p>
  );
}

/** A button as the product draws it: outlined until it is pressed. */
function Act({ pressed, small = false, children }: { pressed: boolean; small?: boolean; children: ReactNode }) {
  return (
    <span className={`inline-block whitespace-nowrap rounded-[3px] border font-medium transition-colors duration-150 ${small ? "px-2 py-[2px] text-[10px]" : "px-3 py-[5px] text-[11.5px]"} ${pressed ? "border-forest bg-forest text-canvas" : "border-forest text-forest"}`}>
      {children}
    </span>
  );
}

function Head({ children }: { children: ReactNode }) {
  return <p className="text-[9.5px] font-medium uppercase tracking-[0.08em] text-stone">{children}</p>;
}

/** The product's window: its name, where the work sits, and the work. */
function Window({ trail, pan, children }: { trail: string[]; pan: number; children: ReactNode }) {
  return (
    <div className="flex h-full flex-col bg-canvas">
      <div className="flex h-[28px] shrink-0 items-center gap-2.5 border-b border-rule-soft px-3.5">
        <span className="font-serif text-[12.5px] font-semibold tracking-[-0.01em] text-ink">Leveri</span>
        <span className="min-w-0 truncate text-[10px] text-stone">{trail.join("  ›  ")}</span>
      </div>
      <Pan x={pan}>{children}</Pan>
    </div>
  );
}

/** A camera move: the content slides up by however much of it is out of view, as `x` runs 0 → 1. */
function Pan({ x, children }: { x: number; children: ReactNode }) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      if (outer.current && inner.current) setRoom(Math.max(0, inner.current.offsetHeight - outer.current.clientHeight));
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (outer.current) observer.observe(outer.current);
    if (inner.current) observer.observe(inner.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={outer} className="min-h-0 flex-1 overflow-hidden">
      <div ref={inner} className="px-3.5 pb-4 pt-3" style={{ transform: `translateY(${Math.round(-room * easeInOut(clamp01(x)))}px)` }}>
        {children}
      </div>
    </div>
  );
}

const ACME_TRAIL = [ACME.client, ACME.period, "Bank reconciliation"];

/* ── 1 · Ask in your own words ─────────────────────────────────────────────── */

const ASK = "Reconcile the bank for Acme Trading, September 2026";
const BANK_STEPS = [
  "Check each statement adds up line by line, and stitch them in date order, noting any gap between them.",
  "Match each cash-book line to one statement line, only where the match is unique.",
  "Name what is left: timing items, items missing from the books, duplicates.",
  "Propose entries for the bank charges and interest the statements show.",
  "Ask for any statement or record that is missing, and record what is still outstanding.",
];
/** The work already on the board when the scene opens, in the board's own group names. */
const BOARD: Array<{ tone: Tone; group: string; title: string }> = [
  { tone: "prog", group: "Waiting for evidence", title: "Lakeside Dental · August 2026 · Bank reconciliation" },
  { tone: "est", group: "Prepared", title: "Harbour Kitchen Ltd · Year to 31 March 2026 · Account preparation" },
  { tone: "prog", group: "In progress", title: "Meridian Stores · September 2026 · Control account reconciliation" },
];
const RECORDS = [
  `Statement ${ACME.firstStatement}.pdf — bank statement`,
  `Statement ${ACME.secondStatement}.pdf — bank statement`,
  "Cash book September.xlsx — cash book",
];

function AskScene({ p }: { p: number }) {
  const sent = p >= 0.35;
  const words = typed(ASK, span(p, 0.04, 0.32));
  return (
    <Window trail={["Home"]} pan={span(p, 0.6, 0.84)}>
      {!sent ? (
        <div className="rounded-[3px] border border-rule-strong bg-raised px-3 py-2.5">
          <p className="min-h-[38px] text-[13px] leading-[1.45] text-ink">
            {words ? words : <span className="text-stone">What can Leveri work on?</span>}
            {words && <span className="ml-[1px] inline-block h-[13px] w-[1.5px] translate-y-[2px] bg-ink" style={{ opacity: caretOn(p) || p > 0.32 && p < 0.34 ? 1 : 0 }} />}
          </p>
        </div>
      ) : null}
      {!sent && (
        <div className="mt-4">
          <Head>Your work</Head>
          <ul className="mt-1 divide-y divide-rule-soft border-y border-rule-soft">
            {BOARD.map((w) => (
              <li key={w.title} className="flex gap-2 py-2">
                <Mark tone={w.tone} />
                <div className="min-w-0">
                  <p className="text-[10px] text-stone">{w.group}</p>
                  <p className="truncate text-[11.5px] text-ink">{w.title}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {sent && (
        <div>
          <p className="text-[10px] text-stone">You asked</p>
          <p className="text-[13px] leading-[1.45] text-ink">{ASK}</p>
        </div>
      )}
      <div className="mt-3 space-y-1">
        <State tone={p < 0.42 ? "prog" : "est"} breath={p < 0.42 ? breathe(p, 30) : undefined} x={span(p, 0.36, 0.39)}>Reading what you asked</State>
        <State tone="est" x={span(p, 0.41, 0.45)}>{ACME.client} · {ACME.period} · 3 records</State>
      </div>
      <div className="mt-3 border border-rule bg-raised" style={appear(span(p, 0.46, 0.53))}>
        <div className="border-b border-rule-soft px-3 pb-2 pt-2.5">
          <p className="text-[10px] text-stone">Here&rsquo;s what I&rsquo;ll do</p>
          <p className="text-[13px] font-medium text-ink">Bank reconciliation</p>
          <p className="text-[10px] text-stone">{ACME.client} · {ACME.period}</p>
        </div>
        <div className="space-y-2.5 px-3 py-2.5">
          <div>
            <Head>Records</Head>
            <ul className="mt-0.5">{RECORDS.map((r) => <li key={r} className="text-[10.5px] leading-[1.5] text-ink">{r}</li>)}</ul>
          </div>
          <ol className="space-y-1.5">
            {BANK_STEPS.map((step, i) => (
              <li key={step} className="grid grid-cols-[14px_minmax(0,1fr)] gap-1.5 text-[10.5px] leading-[1.42] text-ink" style={appear(span(p, 0.54 + i * 0.045, 0.6 + i * 0.045))}>
                <span className="text-stone [font-variant-numeric:tabular-nums]">{i + 1}</span><span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="flex items-center gap-3 border-t border-rule-soft px-3 py-2">
          <Act pressed={p >= 0.86 && p < 0.92}>Approve and start</Act>
          {p >= 0.9 && <span className="flex items-center gap-1.5 text-[10.5px] text-ink" style={appear(span(p, 0.9, 0.94))}><Mark tone="prog" breath={breathe(p, 30)} />Starting the work</span>}
        </div>
      </div>
    </Window>
  );
}

/* ── 2 · Reads the statements ──────────────────────────────────────────────── */

function ReadScene({ p }: { p: number }) {
  const rows = [{ date: "01 Sep", details: "Opening balance", amount: "", balance: ACME.opening.text }, ...ACME.lines];
  const cursor = span(p, 0.08, 0.7) * rows.length;
  return (
    <Window trail={ACME_TRAIL} pan={span(p, 0.74, 0.84)}>
      <div className="flex items-baseline justify-between">
        <p className="font-mono text-[10px] text-stone">Statement {ACME.firstStatement}.pdf</p>
        <p className="font-mono text-[10px] text-stone">page 1 of 3</p>
      </div>
      <div className="mt-1.5 border border-rule bg-raised">
        <p className="border-b border-rule-soft px-2.5 py-1.5 text-[10.5px] text-ink">Statement of account · 1–15 September 2026 · {ACME.currency}</p>
        <div className="grid grid-cols-[36px_minmax(0,1fr)_58px_60px_9px] gap-x-[5px] border-b border-rule-soft px-2.5 py-1 text-[9px] uppercase tracking-[0.06em] text-stone">
          <span>Date</span><span>Details</span><span className="text-right">Amount</span><span className="text-right">Balance</span><span />
        </div>
        {rows.map((row, i) => {
          const current = cursor >= i && cursor < i + 1 && p < 0.7;
          const read = cursor >= i + 1;
          return (
            <div key={`${row.date}-${row.details}`} className={`grid grid-cols-[36px_minmax(0,1fr)_58px_60px_9px] items-center gap-x-[5px] px-2.5 py-[3.5px] text-[10px] transition-colors duration-100 ${current ? "bg-forest-tint" : ""}`}>
              <span className="font-mono text-stone">{row.date}</span>
              <span className="truncate text-ink">{row.details}</span>
              <span className="text-right font-mono text-ink">{row.amount}</span>
              <span className="text-right font-mono text-ink">{row.balance}</span>
              <span className="flex justify-end" style={{ opacity: read ? 1 : 0 }}><Mark tone="est" /></span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 space-y-1">
        <State tone="est" x={span(p, 0.72, 0.76)}>The running balance agrees on every line</State>
        <State tone="est" x={span(p, 0.78, 0.82)}>2 statements read · {ACME.statementLines} lines</State>
        <State tone="att" x={span(p, 0.86, 0.9)}>Between them, no statement for {ACME.gap}</State>
      </div>
    </Window>
  );
}

/* ── 3 · Reconciles, and names what's left ─────────────────────────────────── */

function ReconcileScene({ p }: { p: number }) {
  const matched = counted(ACME.matched, span(p, 0.12, 0.44));
  const named: Array<{ tone: Tone; what: string; why: string }> = [
    { tone: "att", what: `No statement for ${ACME.gap}`, why: `${ACME.gapCashBookLines} cash-book lines fall in the gap · asked of the client` },
    { tone: "prog", what: `${ACME.unpresentedCheque.ref} · ${ACME.unpresentedCheque.text}`, why: "In the cash book only · a candidate timing difference" },
    { tone: "prog", what: `Bank charge · ${ACME.bankCharge.text}`, why: "On the statement only · entry proposed" },
    { tone: "prog", what: `Interest · ${ACME.interest.text}`, why: "On the statement only · entry proposed" },
    { tone: "att", what: `${ACME.duplicateReceipt.from} · ${ACME.duplicateReceipt.text}`, why: "Entered twice in the cash book · for you to confirm" },
  ];
  const figures = [["Statement", ACME.statementClosing.text], ["Cash book", ACME.cashBook.text], ["Difference", ACME.difference.text]] as const;
  return (
    <Window trail={ACME_TRAIL} pan={span(p, 0.72, 0.86)}>
      <div className="grid grid-cols-3 border-y border-rule" style={appear(span(p, 0, 0.08))}>
        {figures.map(([label, value], i) => (
          <div key={label} className={`px-2 py-1.5 ${i > 0 ? "border-l border-rule-soft" : ""}`}>
            <Head>{label}</Head>
            <p className="font-mono text-[11px] text-ink">{ACME.currency} {value}</p>
          </div>
        ))}
      </div>
      <div className="mt-3" style={appear(span(p, 0.08, 0.14))}>
        <div className="flex items-baseline justify-between text-[11.5px] text-ink">
          <span>Matched</span>
          <span className="font-mono">{matched} <span className="text-stone">of {ACME.statementLines} statement lines</span></span>
        </div>
        <div className="mt-1 h-[3px] bg-track"><div className="h-full bg-ink" style={{ width: `${(matched / ACME.statementLines) * 100}%` }} /></div>
      </div>
      <div className="mt-3.5" style={appear(span(p, 0.46, 0.5))}><Head>What is left, named</Head></div>
      <ul className="mt-1 divide-y divide-rule-soft">
        {named.map((n, i) => (
          <li key={n.what} className="flex gap-2 py-1.5" style={appear(span(p, 0.5 + i * 0.05, 0.55 + i * 0.05))}>
            <Mark tone={n.tone} />
            <div className="min-w-0">
              <p className="text-[11.5px] leading-[1.35] text-ink">{n.what}</p>
              <p className="text-[10px] leading-[1.35] text-stone">{n.why}</p>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-2 space-y-1">
        <State tone="est" x={span(p, 0.8, 0.84)}>The difference of {ACME.currency} {ACME.difference.text} is named in full</State>
        <State tone="prog" x={span(p, 0.86, 0.9)}>Waiting for the {ACME.gap} statement</State>
      </div>
    </Window>
  );
}

/* ── 4 · Prepares draft accounts ───────────────────────────────────────────── */

function PrepareScene({ p }: { p: number }) {
  const adopted: Record<string, number> = { farm: 0.36, power: 0.46 };
  return (
    <Window trail={[HARBOUR.client, HARBOUR.period, "Account preparation"]} pan={span(p, 0.5, 0.64)}>
      <div className="flex items-baseline justify-between">
        <p className="text-[12px] font-medium text-ink">Bank lines, by pattern</p>
        <p className="text-[10px] text-stone">{HARBOUR.lineCount} lines · {HARBOUR.currency}</p>
      </div>
      <ul className="mt-1.5 divide-y divide-rule-soft border-y border-rule">
        {HARBOUR.patterns.map((row, i) => {
          const at = adopted[row.key];
          const decided = at !== undefined && p >= at + 0.02;
          return (
            <li key={row.key} className="grid grid-cols-[minmax(0,1fr)_26px_80px_88px] items-center gap-x-1.5 py-[5px]" style={appear(span(p, 0.03 + i * 0.03, 0.08 + i * 0.03))}>
              <span className="text-[10px] leading-[1.3] text-ink">{row.pattern}</span>
              <span className="text-right font-mono text-[9.5px] text-stone">{row.lines}</span>
              <span className="text-right font-mono text-[10px] text-ink">{row.amount}</span>
              <span className="pl-1.5">
                {row.how === "ASKED" ? (
                  <span className="flex items-start gap-1.5"><Mark tone="att" /><span className="text-[10px] leading-[1.3] text-ink">Asked<br /><span className="text-stone">held in suspense</span></span></span>
                ) : row.how === "SUGGESTED" && !decided ? (
                  <span className="block text-[10px] leading-[1.3] text-ink">{row.account}?<br /><Act small pressed={at !== undefined && p >= at - 0.02}>Use this</Act></span>
                ) : (
                  <span className="block text-[10px] leading-[1.3] text-ink">{row.account}<br /><span className="text-stone">{row.how === "RULE" ? "your rule" : row.how === "INVOICES" ? "invoices match" : "you decided"}</span></span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="mt-4" style={appear(span(p, 0.54, 0.58))}>
        <p className="text-[12px] font-medium text-ink">Draft profit and loss</p>
        <p className="text-[10px] text-stone">{HARBOUR.period} · {HARBOUR.currency}</p>
      </div>
      <div className="mt-1.5">
        {HARBOUR.profitAndLoss.map(({ label, text, kind }, i) => (
          <div key={label} className={`flex justify-between py-[2.5px] text-[10.5px] ${kind === "total" ? "border-t border-rule font-medium" : kind === "rule" ? "border-y-[3px] border-double border-rule font-medium" : ""}`} style={appear(span(p, 0.58 + i * 0.03, 0.62 + i * 0.03))}>
            <span className="text-ink">{label}</span><span className="font-mono text-ink">{text}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 space-y-1">
        <State tone="att" x={span(p, 0.82, 0.86)}>Held in suspense, never in the profit: {HARBOUR.suspense.text}</State>
        <State tone="prog" x={span(p, 0.88, 0.92)}>A draft until you review it. Leveri posts nothing.</State>
      </div>
    </Window>
  );
}

/* ── 5 · Asks the client, then carries on ──────────────────────────────────── */

function AskClientScene({ p }: { p: number }) {
  return (
    <Window trail={ACME_TRAIL} pan={span(p, 0.42, 0.82)}>
      <Head>Still needed</Head>
      <div className="mt-1.5 border border-rule bg-raised px-3 py-2.5">
        <p className="flex items-start gap-2 text-[11.5px] text-ink"><Mark tone={p >= 0.52 ? "est" : "att"} />Statement for {ACME.gap}</p>
        <p className="ml-[15px] text-[10px] text-stone">So the {ACME.gapCashBookLines} cash-book lines in the gap can be matched.</p>
        <div className="ml-[15px] mt-2"><Act pressed={p >= 0.12 && p < 0.17}>Create a link for this request</Act></div>
        <div className="ml-[15px] mt-2" style={appear(span(p, 0.18, 0.23))}>
          <p className="font-mono text-[10px] text-ink">app.getleveri.com/r#••••••••••••</p>
          <p className="text-[10px] text-stone">Send it to {ACME.client}. It opens this request and nothing else.</p>
        </div>
      </div>
      <div className="mt-3 ml-6 border border-rule-soft bg-band px-3 py-2" style={appear(span(p, 0.28, 0.34))}>
        <p className="text-[10px] text-stone">{ACME.client} sent</p>
        <p className="flex items-center gap-2 text-[11px] text-ink">
          <span aria-hidden="true" className="inline-block h-[13px] w-[10px] rounded-[1px] border border-rule-strong bg-raised" />
          Statement {ACME.gap}.pdf
        </p>
      </div>
      <div className="mt-3 space-y-1">
        <State tone={p < 0.5 ? "prog" : "est"} breath={p < 0.5 ? breathe(p, 24) : undefined} x={span(p, 0.38, 0.42)}>Version 2 · the same work, reading the new statement</State>
        <State tone="est" x={span(p, 0.5, 0.54)}>The {ACME.gapCashBookLines} lines in the gap match</State>
        <State tone="est" x={span(p, 0.58, 0.62)}>Coverage complete, 1–30 Sep</State>
        <State tone="est" x={span(p, 0.66, 0.7)}>Difference {ACME.currency} {ACME.difference.text}, named in full by 4 items</State>
      </div>
      <div className="mt-3.5" style={appear(span(p, 0.76, 0.82))}>
        <Head>Your review</Head>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {["Accept", "Adjust", "Reject", "Request evidence"].map((a) => <Act key={a} small pressed={false}>{a}</Act>)}
        </div>
      </div>
    </Window>
  );
}

export interface Scene { key: string; title: string; line: string; ms: number; Draw: (props: { p: number }) => JSX.Element }

export const SCENES: readonly Scene[] = [
  { key: "ask", title: "Ask in your own words", line: "Name the client and the period. Leveri shows its plan, and starts when you approve.", ms: 11_000, Draw: AskScene },
  { key: "read", title: "Reads the statements", line: "PDF statements and bank exports. A line is used only when the statement’s own running balance agrees.", ms: 12_000, Draw: ReadScene },
  { key: "reconcile", title: "Reconciles, and names what’s left", line: "Every line ends matched, explained or asked about. A difference is never plugged.", ms: 13_000, Draw: ReconcileScene },
  { key: "prepare", title: "Prepares draft accounts", line: "From bank statements: lines grouped by pattern, an account suggested for each, and you decide.", ms: 13_000, Draw: PrepareScene },
  { key: "client", title: "Asks the client, then carries on", line: "A link for exactly what’s missing. When the file arrives, the same work picks up where it stopped.", ms: 11_000, Draw: AskClientScene },
];
